import { useEffect } from 'react';

// Injected at build time by Vite `define` (see vite.config.ts) — the id of the
// build this page was loaded from (git commit SHA on Vercel, timestamp locally).
declare const __BUILD_ID__: string;

// How often the kiosk checks whether a newer build has been deployed.
const CHECK_INTERVAL_MS = 60_000;

/**
 * Self-update for the wall-mounted kiosk. The kiosk loads once at boot and runs
 * indefinitely, so without this it keeps showing whatever bundle it started
 * with until someone manually reloads it (SSH + restart). This polls the
 * deploy's `/version.json` and reloads the page when the live build id no longer
 * matches the running one — so any future deploy propagates to the kiosk on its
 * own within ~a minute.
 *
 * PROD-only: the dev server doesn't emit version.json and Vite HMR already
 * handles updates. `version.json` is fetched with cache-busting + `no-store`,
 * and the kiosk service worker passes it straight through (it only caches
 * navigations / hashed assets / the sheet API), so the check always sees the
 * live value.
 */
export function useKioskAutoUpdate(): void {
  useEffect(() => {
    if (!import.meta.env.PROD) return;

    async function check() {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, {
          cache: 'no-store',
        });
        if (!res.ok) return;
        const data = (await res.json()) as { id?: string };
        if (data.id && data.id !== __BUILD_ID__) {
          window.location.reload();
        }
      } catch {
        // Offline or a transient blip — just try again next interval.
      }
    }

    // Check shortly after load (catches a deploy that landed while the kiosk
    // was booting), then on a steady interval.
    const initial = window.setTimeout(() => void check(), 5_000);
    const interval = window.setInterval(() => void check(), CHECK_INTERVAL_MS);
    return () => {
      clearTimeout(initial);
      clearInterval(interval);
    };
  }, []);
}
